import React from 'react';
import PageMeta from '../PageMeta';

const LEGAL_META = {
  privacy: {
    title: 'Privacy Policy',
    description: 'How RentKollect (Ramde Business Solutions LLP) collects, uses, and protects your personal data. Compliant with India DPDP Act 2023 and EU GDPR.',
  },
  terms: {
    title: 'Terms & Conditions',
    description: 'Terms of Service for RentKollect — the WhatsApp-first PG management platform operated by Ramde Business Solutions LLP.',
  },
  deletion: {
    title: 'Data Deletion Instructions',
    description: 'How to delete your RentKollect account and all associated data. Request data deletion by email or WhatsApp — processed within 30 days.',
  },
  refund: {
    title: 'Cancellation & Refund Policy',
    description: 'RentKollect (Ramde Business Solutions LLP) cancellation and refund policy for wallet top-ups, subscriptions and premium services. Refunds processed in 1–2 business days.',
  },
};

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-bold text-gray-900 mb-3">{title}</h2>
    <div className="text-gray-700 text-[15px] leading-relaxed space-y-3">{children}</div>
  </section>
);

const LegalPage = ({ type }) => {
  const isPrivacy = type === 'privacy';
  const isDeletion = type === 'deletion';
  const isRefund = type === 'refund';
  const today = 'April 24, 2026';
  const meta = LEGAL_META[type] || LEGAL_META.terms;

  const canonicalMap = {
    privacy: 'privacy',
    terms: 'terms',
    deletion: 'data-deletion',
    refund: 'refund',
  };

  const headingMap = {
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    deletion: 'Data Deletion Instructions',
    refund: 'Cancellation & Refund Policy',
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`${type}-page`}>
      <PageMeta
        title={meta.title}
        description={meta.description}
        canonical={`https://rentkollect.com/${canonicalMap[type] || type}`}
      />
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-green-600 to-teal-600 text-white py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <a href="/" className="text-emerald-100 hover:text-white text-sm font-semibold">← Back to RentKollect</a>
          <h1 className="text-4xl font-bold mt-3">{headingMap[type] || 'Terms of Service'}</h1>
          <p className="text-emerald-100 mt-2 text-sm">Last updated: {today}</p>
          <p className="text-emerald-50 mt-1 text-[13px]" data-testid="legal-entity-banner">
            RentKollect is a brand of <strong>Ramde Business Solutions LLP</strong> · LLPIN ACR-9891 · GST 06ABMFR3856J1ZO
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        {isPrivacy
          ? <PrivacyBody />
          : isDeletion
            ? <DataDeletionBody />
            : isRefund
              ? <RefundBody />
              : <TermsBody />}

        <div className="mt-12 pt-8 border-t border-gray-200 text-sm text-gray-600 space-y-1">
          <p className="font-semibold text-gray-800 mb-2">Legal entity & contact</p>
          <p><strong>Ramde Business Solutions LLP</strong> (operating as <strong>RentKollect</strong>)</p>
          <p>LLPIN: ACR-9891 · GSTIN: 06ABMFR3856J1ZO</p>
          <p>Registered office: H.No 59, Neelkanth Enclave, near Ekta Hospital, Badshahpur, Gurgaon, Haryana 122101, India</p>
          <p>Email: <a href="mailto:hello@rentkollect.com" className="text-emerald-600 hover:underline">hello@rentkollect.com</a></p>
          <p>WhatsApp: <a href="https://wa.me/919821922296" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">+91 98219 22296</a></p>
        </div>
      </div>
    </div>
  );
};

const PrivacyBody = () => (
  <>
    <Section title="1. About this policy">
      <p>
        RentKollect ("we", "our", "us") is a brand operated by <strong>Ramde Business Solutions LLP</strong>,
        a limited liability partnership registered in India (LLPIN: ACR-9891, GSTIN: 06ABMFR3856J1ZO). We operate
        the rentkollect.com website, a web dashboard, and a WhatsApp-first AI assistant ("Kolly") that helps
        PG / hostel / co-living owners manage their properties. This policy explains what personal data we collect,
        how we use it, and your rights under India's Digital Personal Data Protection Act 2023 and the
        EU GDPR (where applicable).
      </p>
    </Section>

    <Section title="2. What we collect">
      <p><strong>From property owners (our customers):</strong></p>
      <ul className="list-disc ml-6 space-y-1">
        <li>Name, mobile number, email, city</li>
        <li>Property details (name, address, rooms, beds, photos you upload)</li>
        <li>Tenant records you create (name, phone, Aadhaar, rent, deposit)</li>
        <li>WhatsApp messages you send to Kolly, and our replies (stored as a conversation log)</li>
        <li>Payment transaction metadata (if/when Razorpay is enabled)</li>
      </ul>
      <p><strong>From tenants (via the self-onboarding link owners share):</strong></p>
      <ul className="list-disc ml-6 space-y-1">
        <li>Name, date of birth, email, permanent address</li>
        <li>Emergency contact name and number</li>
        <li>Aadhaar number and uploaded Aadhaar front/back/selfie images</li>
        <li>IP address and timestamp at the moment of submitting the rental agreement</li>
      </ul>
      <p><strong>Automatically:</strong> Google Analytics 4 (G-C2X5GXCJ88) measures page visits. We don't
        use third-party advertising cookies.</p>
    </Section>

    <Section title="3. How we use your data">
      <ul className="list-disc ml-6 space-y-1">
        <li>To provide the service — render your dashboard, run Kolly's AI replies, process rent/deposit records</li>
        <li>To share tenant data with the property owner who invited them (and with no-one else)</li>
        <li>To improve Kolly's intent parsing — we may use aggregated, anonymised conversation logs to
            fine-tune models. Your raw messages are never shared with third parties outside of the LLM
            provider processing them in real time (Anthropic Claude via Emergent)</li>
        <li>To send you product and transactional communications on WhatsApp / email</li>
      </ul>
    </Section>

    <Section title="4. Who we share data with">
      <p>We do not sell your data. We share the minimum necessary with:</p>
      <ul className="list-disc ml-6 space-y-1">
        <li><strong>Meta (WhatsApp Cloud API)</strong> — to deliver messages to/from you</li>
        <li><strong>Anthropic / Google / OpenAI (via Emergent Universal Key)</strong> — to run Kolly's
            language understanding. Messages are sent in real time, not stored by the provider for training</li>
        <li><strong>MongoDB Atlas</strong> — encrypted-at-rest database hosting in India region</li>
        <li><strong>Google Analytics</strong> — page view analytics only (IP anonymised)</li>
        <li><strong>Razorpay</strong> — when/if you make payments. Full PCI-DSS compliant</li>
      </ul>
    </Section>

    <Section title="5. How long we keep data">
      <ul className="list-disc ml-6 space-y-1">
        <li>Active owner accounts — for as long as you have an account</li>
        <li>Tenant records — as long as the owner's account is active + 3 years after tenant exit
          (needed for Indian rental / tax compliance)</li>
        <li>WhatsApp conversation logs — 90 days</li>
        <li>If you request deletion, we comply within 30 days (see §7)</li>
      </ul>
    </Section>

    <Section title="6. How we secure your data">
      <ul className="list-disc ml-6 space-y-1">
        <li>All traffic uses HTTPS/TLS 1.3</li>
        <li>Database encryption at rest (AES-256) and in transit</li>
        <li>Aadhaar images stored with strict access controls — only the property owner who invited the
            tenant can view them</li>
        <li>Employee access to production data is role-limited and audit-logged</li>
      </ul>
    </Section>

    <Section title="7. Your rights">
      <p>Under DPDP 2023 / GDPR you have the right to:</p>
      <ul className="list-disc ml-6 space-y-1">
        <li>Access a copy of the data we hold on you</li>
        <li>Request correction of inaccurate data</li>
        <li>Request deletion ("right to be forgotten")</li>
        <li>Withdraw consent for WhatsApp / email communications (reply STOP or email us)</li>
        <li>Lodge a complaint with the Data Protection Board of India</li>
      </ul>
      <p>
        To exercise any of these rights, email <a href="mailto:privacy@rentkollect.com" className="text-emerald-600 hover:underline">privacy@rentkollect.com</a> with
        your registered mobile number. We respond within 14 working days.
      </p>
    </Section>

    <Section title="8. Children">
      <p>RentKollect is not intended for users under 18. We do not knowingly collect data from minors.</p>
    </Section>

    <Section title="9. Changes to this policy">
      <p>
        We'll notify you on WhatsApp and via banner on rentkollect.com if we make material changes.
        Continuing to use the service after the change means you accept the updated policy.
      </p>
    </Section>
  </>
);

const TermsBody = () => (
  <>
    <Section title="1. Acceptance">
      <p>
        By using RentKollect (the website, dashboard, or WhatsApp assistant "Kolly"),
        you enter into a binding agreement with <strong>Ramde Business Solutions LLP</strong>
        (LLPIN ACR-9891, GSTIN 06ABMFR3856J1ZO), operating the service under the brand name
        "RentKollect". If you don't agree with these terms, please don't use the service.
      </p>
    </Section>
    <Section title="2. Who can use the service">
      <p>
        RentKollect is offered to PG, hostel, and co-living owners in India who are at least
        18 years old and legally entitled to rent out the properties they add to the platform.
      </p>
    </Section>
    <Section title="3. Your responsibilities">
      <ul className="list-disc ml-6 space-y-1">
        <li>Provide accurate information about yourself and your properties</li>
        <li>Obtain clear consent from tenants before submitting their KYC / Aadhaar details via Kolly</li>
        <li>Comply with applicable state and municipal rental laws, police-verification requirements,
            and GST obligations</li>
        <li>Not use RentKollect for illegal rentals, discriminatory practices, or to upload unlawful content</li>
      </ul>
    </Section>
    <Section title="4. Our responsibilities">
      <ul className="list-disc ml-6 space-y-1">
        <li>Provide the service with commercially reasonable availability</li>
        <li>Safeguard your data as described in the Privacy Policy</li>
        <li>Notify you of any security breach involving your data within 72 hours</li>
      </ul>
    </Section>
    <Section title="5. Beta disclaimer">
      <p>
        RentKollect is currently in beta. Features may change, break, or be withdrawn without
        notice. We do our best to give warning. Your data is always backed up.
      </p>
    </Section>
    <Section title="6. Payment & refunds">
      <p>
        During beta, the service is free. Once paid plans launch, we'll give all existing
        users 30 days' notice plus special pricing. All subscriptions are billed in Indian Rupees.
      </p>
    </Section>
    <Section title="7. Limitation of liability">
      <p>
        To the maximum extent permitted by law, Ramde Business Solutions LLP's liability for any
        claim arising out of the RentKollect service is limited to the fees paid in the 3 months
        preceding the claim. We are not liable for indirect, consequential, or punitive damages.
      </p>
    </Section>
    <Section title="8. Termination">
      <p>
        You can close your account anytime by WhatsApping Kolly with "close my account" or emailing
        us. We can terminate your account for violations of these terms with reasonable notice.
      </p>
    </Section>
    <Section title="9. Governing law">
      <p>
        These terms are governed by the laws of India. Any disputes involving Ramde Business
        Solutions LLP will be resolved exclusively in the courts of Gurgaon, Haryana.
      </p>
    </Section>
  </>
);

export const PrivacyPolicy = () => <LegalPage type="privacy" />;
export const TermsOfService = () => <LegalPage type="terms" />;
export const DataDeletion = () => <LegalPage type="deletion" />;
export const RefundPolicy = () => <LegalPage type="refund" />;

const DataDeletionBody = () => (
  <>
    <Section title="How to delete your RentKollect data">
      <p>
        We respect your right to have your personal data removed from our systems, in compliance
        with India's Digital Personal Data Protection Act 2023 and Meta's platform policies.
        This page explains exactly how.
      </p>
    </Section>

    <Section title="Option 1 — WhatsApp (fastest, under 30 seconds)">
      <p>
        Open your WhatsApp chat with Kolly (RentKollect's assistant) and send the message:
      </p>
      <pre className="bg-gray-100 rounded-lg p-3 font-mono text-sm">delete my account</pre>
      <p>
        Kolly will confirm the request and permanently remove your data within 24 hours. You'll
        receive one final WhatsApp message confirming deletion.
      </p>
    </Section>

    <Section title="Option 2 — Email">
      <p>
        Email <a href="mailto:privacy@rentkollect.com" className="text-emerald-600 hover:underline">privacy@rentkollect.com</a> from
        the email address linked to your account, with the subject line:
      </p>
      <pre className="bg-gray-100 rounded-lg p-3 font-mono text-sm">DATA DELETION REQUEST</pre>
      <p>
        In the body, include your registered mobile number. We'll verify your identity (via a
        WhatsApp OTP) and complete the deletion within 14 working days. You'll receive a
        confirmation email once done.
      </p>
    </Section>

    <Section title="What gets deleted">
      <ul className="list-disc ml-6 space-y-1">
        <li>Your owner profile (name, phone, email, city)</li>
        <li>All properties, rooms, and photos you created</li>
        <li>All tenant records, KYC documents (Aadhaar photos), and agreements</li>
        <li>All payment, expense, and due records</li>
        <li>Your entire WhatsApp conversation log with Kolly</li>
        <li>Any stored session / preference data</li>
      </ul>
    </Section>

    <Section title="What we retain (and why)">
      <p>
        To comply with Indian tax and rental laws, we may retain the following for up to
        3 years in an encrypted, access-restricted archive:
      </p>
      <ul className="list-disc ml-6 space-y-1">
        <li>GST invoices issued to you (if any)</li>
        <li>Payment transaction IDs (Razorpay/bank) — required for reconciliation audits</li>
        <li>An anonymised record that "a deletion request was fulfilled" (no personal data, just the date)</li>
      </ul>
      <p>
        Once the statutory retention period ends, we delete these too. Nothing is retained indefinitely.
      </p>
    </Section>

    <Section title="Tenant data — special note">
      <p>
        If you're a <strong>tenant</strong> whose PG owner submitted your Aadhaar through Kolly's
        onboarding link, you can request deletion of your own record independently. Email
        <a href="mailto:privacy@rentkollect.com" className="text-emerald-600 hover:underline"> privacy@rentkollect.com</a> from
        your own email, or WhatsApp us at <a href="https://wa.me/919821922296" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">+91 98219 22296</a> with:
      </p>
      <ul className="list-disc ml-6 space-y-1">
        <li>Your full name</li>
        <li>The PG / property name you were / are staying at</li>
        <li>Your registered mobile number</li>
      </ul>
      <p>
        We'll verify and delete your record within 14 working days. Your PG owner will be
        informed that the record was deleted at your request.
      </p>
    </Section>

    <Section title="Questions or concerns">
      <p>
        If any part of this process is unclear or you believe your request hasn't been actioned,
        email <a href="mailto:privacy@rentkollect.com" className="text-emerald-600 hover:underline">privacy@rentkollect.com</a>.
        You also have the right to lodge a complaint with the Data Protection Board of India.
      </p>
    </Section>
  </>
);

const RefundBody = () => (
  <>
    <Section title="Our commitment">
      <p>
        <strong>RAMDE BUSINESS SOLUTIONS LLP</strong> believes in helping its customers as far as possible,
        and has therefore a liberal cancellation policy. Under this policy:
      </p>
    </Section>

    <Section title="Cancellations">
      <ul className="list-disc ml-6 space-y-3">
        <li>
          Cancellations will be considered only if the request is made immediately after placing the order.
          However, the cancellation request may not be entertained if the orders have been communicated to
          the vendors/merchants and they have initiated the process of shipping them.
        </li>
        <li>
          RAMDE BUSINESS SOLUTIONS LLP does not accept cancellation requests for perishable items like
          flowers, eatables etc. However, refund/replacement can be made if the customer establishes that
          the quality of product delivered is not good.
        </li>
      </ul>
    </Section>

    <Section title="Damaged or defective items">
      <ul className="list-disc ml-6 space-y-3">
        <li>
          In case of receipt of damaged or defective items please report the same to our Customer Service
          team. The request will, however, be entertained once the merchant has checked and determined
          the same at his own end. This should be reported within <strong>7 Days</strong> of receipt of
          the products.
        </li>
        <li>
          In case you feel that the product received is not as shown on the site or as per your
          expectations, you must bring it to the notice of our customer service within <strong>7 Days</strong>
          of receiving the product. The Customer Service Team after looking into your complaint will take
          an appropriate decision.
        </li>
      </ul>
    </Section>

    <Section title="Warranty items">
      <p>
        In case of complaints regarding products that come with a warranty from manufacturers, please
        refer the issue to them.
      </p>
    </Section>

    <Section title="Refund processing">
      <p>
        In case of any refunds approved by RAMDE BUSINESS SOLUTIONS LLP, it will take <strong>1–2 Days</strong>
        for the refund to be processed to the end customer.
      </p>
    </Section>

    <Section title="How to raise a refund request">
      <p>
        Email <a href="mailto:hello@rentkollect.com" className="text-emerald-600 hover:underline">hello@rentkollect.com</a> or
        WhatsApp <a href="https://wa.me/919821922296" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">+91 98219 22296</a> with:
      </p>
      <ul className="list-disc ml-6 space-y-1">
        <li>Your registered mobile number</li>
        <li>Order / transaction ID (from your wallet top-up or subscription)</li>
        <li>Reason for refund</li>
      </ul>
    </Section>
  </>
);

export default PrivacyPolicy;
